import { useDispatch, useSelector } from 'react-redux';
import { useBooleanFlagValue } from '@rsos/feature-flags';
import useHasAccessToAirAmbulance from '@rsos/hooks/useHasAccessToAirAmbulance';
import useHasAccessToIntlCallback from '@rsos/hooks/useHasAccessToIntlCallback';
import { OVERVIEW_TAB } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneTabs';
import {
  openResourcePanel,
  setResourcePanelView,
  setSelectedRequest,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import isValidCoordinates from '@rsos/utils/isValidCoordinates';
import {
  trackRequestAirAmbulanceClick,
  AIR_AMBULANCE_SOURCE_BUTTON,
} from '../../../../../gainsightTrackings/airAmbulanceTrackings';
import useHasAccessToCommunicatorTranscription from '../../../../../hooks/useHasAccessToCommunicatorTranscription';
import useIsFordCall from '../../../../../hooks/useIsFordCall';
import AdrQueryContainer from '../../../../AdditionalData/AdrQuery/AdrQueryContainer';
import MediaUniteEventPane from '../../../../AdditionalData/Media/MediaUniteEventPane';
import MostRecentLocationCard from '../../../../MostRecentLocationCard';
import AirAmbulanceCard from '../../AirAmbulance';
import ChatCard from '../../ChatCard';
import EventShareCard from '../../EventShareCard/EventShareCard';
import { OverviewIntlCallbackCard } from '../../IntlCallback';
import PreviousCallsCard from '../../PreviousCallsCard';
import DataCardsSummary from '../DataCardsSummary';
import {
  selectPreviousTranscriptionsForCall,
  selectTranscriptionForCall,
} from '../TranscriptTab/transcriptionSlice';
import OverviewBanners from './OverviewBanners';
import { OverviewTabContent, FullWidthContent } from './OverviewTab.styles';

/**
 * The Overview tab of the EventPane
 */
const OverviewTab = ({
  onDataCardClick,
  callerID,
  onOpenRecentTranscripts,
}) => {
  const hasAccessToAirAmbulance = useHasAccessToAirAmbulance();
  const hasAccessToIntlCallback = useHasAccessToIntlCallback();
  const isFord = useIsFordCall(callerID);
  const showRecentTranscripts = useBooleanFlagValue(
    'show_recent_transcripts',
    false,
  );
  const hasAccessToCommunicatorTranscript =
    useHasAccessToCommunicatorTranscription();

  const selectedCall = useSelector(
    state => state.emergencyCalls?.callQueue?.[callerID],
  );

  // Get recent transcripts data
  const previousTranscriptions = useSelector(state =>
    showRecentTranscripts
      ? selectPreviousTranscriptionsForCall(callerID)(state)
      : [],
  );
  const currentTranscription = useSelector(state =>
    selectTranscriptionForCall(callerID)(state),
  );
  const totalRecords = useSelector(
    state => state?.transcriptions?.transcriptionTotals?.[callerID],
  );

  // Calculate previous transcripts count
  const callStartTime =
    selectedCall?.core?.locations?.[0]?.call_start_time ||
    selectedCall?.core?.locations?.[0]?.conversation_start_time;
  const seededCallStartTime =
    currentTranscription?.payload?.call_information?.call_start_time || null;
  const hasCurrentTranscriptionSeeded =
    typeof callStartTime === 'number' &&
    typeof seededCallStartTime === 'number' &&
    seededCallStartTime > 0 &&
    Math.abs(callStartTime - seededCallStartTime) < 10 * 60 * 1000;
  const adjustedTotalCount =
    typeof totalRecords === 'number'
      ? Math.max(0, totalRecords - (hasCurrentTranscriptionSeeded ? 1 : 0))
      : null;
  const previousTotalCount =
    adjustedTotalCount === null
      ? previousTranscriptions.length
      : Math.max(adjustedTotalCount, previousTranscriptions.length);
  const canShowPreviousCalls =
    !isFord &&
    showRecentTranscripts &&
    hasAccessToCommunicatorTranscript &&
    previousTotalCount > 0;

  const mostRecentLoc = getMostRecentLoc(selectedCall);
  const hasLocationToRender = isValidCoordinates(mostRecentLoc);

  const dispatch = useDispatch();
  const handleAirAmbulanceResourcePanel = () => {
    dispatch(setSelectedRequest(null));
    dispatch(openResourcePanel());
    dispatch(setResourcePanelView('air_ambulance'));
    trackRequestAirAmbulanceClick({
      sourceButton: AIR_AMBULANCE_SOURCE_BUTTON.OVERVIEW_TAB,
    });
  };

  return (
    <>
      <OverviewTabContent horizontal>
        <OverviewBanners />
        <AdrQueryContainer />
        {!selectedCall?.isAdmin && hasLocationToRender && (
          <MostRecentLocationCard />
        )}
        {canShowPreviousCalls && (
          <FullWidthContent
            backgroundColor="background"
            data-name="overview-previous-calls-card"
            noPadding
          >
            <PreviousCallsCard
              previousCallsCount={previousTotalCount}
              onClick={onOpenRecentTranscripts}
            />
          </FullWidthContent>
        )}
        <DataCardsSummary onDataCardClick={onDataCardClick} />
        <EventShareCard />
        <ChatCard />
        <MediaUniteEventPane />
        {hasAccessToAirAmbulance && (
          <AirAmbulanceCard
            tab={OVERVIEW_TAB}
            handleClick={handleAirAmbulanceResourcePanel}
          />
        )}
        {hasAccessToIntlCallback && !isFord && <OverviewIntlCallbackCard />}
      </OverviewTabContent>
    </>
  );
};

export default OverviewTab;
